import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import type { CutscenePlayer, CutsceneSequence } from '@/gameplay/Cutscene';
import { buildCharacter } from '@/meshkit/character/build';
import { BASE_PROPORTIONS, PROCEDURAL_TRI_BUDGET, RIVAL_SOLDIER, type CharacterSpec } from '@/meshkit/character/spec';
import {
  applyMissionRewards,
  missionCompletedFlag,
  type MissionDefinition,
  type MissionProgressState,
} from '@/gameplay/Mission';
import { HARBOR } from '@/world/districts/harbor';

export const PROLOGUE_MISSION_ID = 'na-00-het-diner';

export const PROLOGUE_MISSION: MissionDefinition = {
  id: PROLOGUE_MISSION_ID,
  title: 'Het Diner',
  act: 0,
  beats: [
    {
      id: 'setup',
      kind: 'setup',
      title: 'Familielunch bij Aldo',
      objective: 'Listen to the family before the city turns',
      setup: 'A quiet table scene: Luca is loved, but not yet treated as the next Don.',
    },
    {
      id: 'street-objective',
      kind: 'streetObjective',
      title: 'De havenmeeting',
      objective: 'Ride with Aldo to the dockside meeting',
      trigger: 'Cutscene camera moves from safehouse warmth to the cold quay.',
    },
    {
      id: 'human-beat',
      kind: 'humanBeat',
      title: 'Aldo houdt Luca klein',
      objective: 'Aldo tells Luca to watch, not speak',
      onEnter: 'Aldo protects Luca by underestimating him in front of the old men.',
    },
    {
      id: 'complication',
      kind: 'complication',
      title: 'Barili komt te vroeg',
      objective: 'The meeting collapses before anyone can negotiate',
      trigger: 'A shot, a car door, sodium light on wet concrete.',
    },
    {
      id: 'choice-or-consequence',
      kind: 'choiceOrConsequence',
      title: 'Geen keuze',
      objective: 'Luca survives because someone pulls him down',
      onComplete: 'The story begins with consequence, not heroics.',
    },
    {
      id: 'reward',
      kind: 'reward',
      title: 'Bloed op de kade',
      objective: 'Find whoever is still loyal',
      onComplete: 'Mission 1 unlocks at the harbor safehouse.',
    },
  ],
  rewards: {
    flags: {
      [missionCompletedFlag(PROLOGUE_MISSION_ID)]: true,
      'prologue.dinnerSeen': true,
    },
  },
};

export interface PrologueMissionConfig {
  readonly definition?: MissionDefinition;
  readonly state: MissionProgressState;
  readonly cutscene: CutscenePlayer;
  readonly scene?: THREE.Scene;
  readonly playerObject?: THREE.Object3D;
  readonly onComplete?: () => void;
}

export class PrologueMission {
  readonly definition: MissionDefinition;
  readonly #state: MissionProgressState;
  readonly #cutscene: CutscenePlayer;
  readonly #scene: THREE.Scene | undefined;
  readonly #playerObject: THREE.Object3D | undefined;
  readonly #onComplete: (() => void) | undefined;
  readonly #unsubscribers: Array<() => void> = [];
  readonly #actors = new THREE.Group();
  #playerRestore: { position: THREE.Vector3; rotation: THREE.Euler; visible: boolean } | null = null;
  #aldo: THREE.Object3D | null = null;
  #shooter: THREE.Object3D | null = null;
  #completed = false;

  constructor(config: PrologueMissionConfig) {
    this.definition = config.definition ?? PROLOGUE_MISSION;
    this.#state = config.state;
    this.#cutscene = config.cutscene;
    this.#scene = config.scene;
    this.#playerObject = config.playerObject;
    this.#onComplete = config.onComplete;
    this.#actors.name = 'NA_Prologue_Actors';
  }

  get id(): string {
    return this.definition.id;
  }

  start(): void {
    this.dispose();
    this.#completed = false;
    this.#setupActors();
    this.#unsubscribers.push(
      bus.on('cutscene:ended', ({ id }) => {
        if (id === this.id) this.#complete();
      }),
      bus.on('cutscene:skipped', ({ id }) => {
        if (id === this.id) this.#complete();
      }),
    );
    bus.emit('mission:objectiveChanged', { text: 'Watch the dinner at Aldo Vitale’s table' });
    this.#cutscene.play(createPrologueSequence(this.definition, {
      revealShooter: () => this.#revealShooter(),
      fireShot: () => this.#fireShot(),
      killAldo: () => this.#killAldo(),
    }));
  }

  skip(): void {
    this.#cutscene.skip();
  }

  dispose(): void {
    for (const unsubscribe of this.#unsubscribers) unsubscribe();
    this.#unsubscribers.length = 0;
    this.#teardownActors();
  }

  #complete(): void {
    if (this.#completed) return;
    this.#completed = true;
    applyMissionRewards(this.definition, this.#state);
    this.#state.setFlag(missionCompletedFlag(this.id), true);
    bus.emit('mission:completed', { id: this.id });
    this.dispose();
    this.#onComplete?.();
  }

  #setupActors(): void {
    if (!this.#scene) return;
    this.#actors.clear();
    this.#actors.position.set(0, 0, 0);
    this.#scene.add(this.#actors);

    this.#aldo = characterObject(ALDO_SPEC);
    this.#aldo.name = 'NA_Prologue_Aldo';
    this.#aldo.position.set(PROLOGUE_STAGING.aldo.x, 0, PROLOGUE_STAGING.aldo.y);
    this.#aldo.rotation.y = Math.PI * 0.9;
    this.#actors.add(this.#aldo);

    this.#shooter = characterObject(RIVAL_SOLDIER);
    this.#shooter.name = 'NA_Prologue_BariliShooter';
    this.#shooter.position.set(PROLOGUE_STAGING.shooter.x, 0, PROLOGUE_STAGING.shooter.y);
    this.#shooter.rotation.y = -Math.PI * 0.35;
    this.#shooter.visible = false;
    this.#actors.add(this.#shooter);

    if (this.#playerObject) {
      this.#playerRestore = {
        position: this.#playerObject.position.clone(),
        rotation: this.#playerObject.rotation.clone(),
        visible: this.#playerObject.visible,
      };
      this.#playerObject.position.set(PROLOGUE_STAGING.luca.x, 0, PROLOGUE_STAGING.luca.y);
      this.#playerObject.rotation.y = Math.PI * 0.75;
      this.#playerObject.visible = true;
    }
  }

  #teardownActors(): void {
    this.#scene?.remove(this.#actors);
    this.#actors.clear();
    this.#aldo = null;
    this.#shooter = null;
    if (this.#playerObject && this.#playerRestore) {
      this.#playerObject.position.copy(this.#playerRestore.position);
      this.#playerObject.rotation.copy(this.#playerRestore.rotation);
      this.#playerObject.visible = this.#playerRestore.visible;
    }
    this.#playerRestore = null;
  }

  #revealShooter(): void {
    if (this.#shooter) this.#shooter.visible = true;
  }

  #fireShot(): void {
    this.#revealShooter();
    bus.emit('combat:shot', {
      x: PROLOGUE_STAGING.shooter.x,
      z: PROLOGUE_STAGING.shooter.y,
      weapon: 'Revolver',
      faction: 'barili',
    });
  }

  #killAldo(): void {
    if (!this.#aldo) return;
    this.#aldo.rotation.set(Math.PI / 2, Math.PI * 0.9, -0.22);
    this.#aldo.position.set(PROLOGUE_STAGING.aldo.x + 0.2, 0.08, PROLOGUE_STAGING.aldo.y + 0.35);
    bus.emit('combat:impact', {
      x: PROLOGUE_STAGING.aldo.x,
      z: PROLOGUE_STAGING.aldo.y,
      outcome: 'target',
    });
  }
}

const PROLOGUE_STAGING = {
  dinner: new THREE.Vector2(HARBOR.safehouse.x, HARBOR.safehouse.y),
  luca: new THREE.Vector2(HARBOR.playerSpawn.x - 1.4, HARBOR.playerSpawn.y + 0.7),
  aldo: new THREE.Vector2(HARBOR.playerSpawn.x + 0.9, HARBOR.playerSpawn.y + 0.15),
  shooter: new THREE.Vector2(HARBOR.playerSpawn.x + 6.2, HARBOR.playerSpawn.y - 2.6),
} as const;

/**
 * Don Aldo: een oudere, zwaardere man dan zijn zoon. Grijs, kalend, snor, gesloten donkere
 * overjas met gouden accenten — de Don leest als gewicht, niet als snelheid.
 */
const ALDO_SPEC: CharacterSpec = {
  name: 'Aldo_Vitale',
  proportions: {
    ...BASE_PROPORTIONS,
    crownHeight: 1.76,
    shoulderWidth: 0.53,
    waistWidth: 0.36,
    hipWidth: 0.39,
    bellyDepth: 0.05,
    jawWidth: 0.9,
    jawSquareness: 0.4,
    noseSize: 1.15,
  },
  colors: {
    skin: 'skinShadow',
    hair: 'hairGrey',
    coat: 'charcoal',
    shirt: 'cream',
    trousers: 'charcoalOlive',
    shoes: 'charcoal',
    accent: 'gold',
    hat: 'charcoal',
  },
  features: {
    hair: 'balding',
    hat: 'none',
    facialHair: 'moustache',
    coatStyle: 'overcoat',
    openCoat: false,
    glasses: false,
    chain: true,
    watch: true,
    tie: true,
  },
  triBudget: PROCEDURAL_TRI_BUDGET,
};

function characterObject(spec: CharacterSpec): THREE.Group {
  return buildCharacter(spec).root;
}

export interface PrologueSequenceActions {
  readonly revealShooter?: () => void;
  readonly fireShot?: () => void;
  readonly killAldo?: () => void;
}

function stagingFocus(a: THREE.Vector2, b: THREE.Vector2): THREE.Vector3 {
  return new THREE.Vector3((a.x + b.x) * 0.5, 0, (a.y + b.y) * 0.5);
}

export function createPrologueSequence(
  definition: MissionDefinition = PROLOGUE_MISSION,
  actions: PrologueSequenceActions = {},
): CutsceneSequence {
  return {
    id: definition.id,
    restoreCamera: true,
    beats: [
      {
        duration: 4.2,
        camera: { focus: new THREE.Vector3(HARBOR.safehouse.x, 0, HARBOR.safehouse.y), viewHeight: 27, ease: 'smooth' },
        text: {
          speaker: 'Aldo Vitale',
          line: 'A family eats first. Business can wait outside in the cold.',
        },
      },
      {
        duration: 3.8,
        camera: { focus: new THREE.Vector3(HARBOR.safehouse.x + 5, 0, HARBOR.safehouse.y - 4), viewHeight: 29, ease: 'smooth' },
        text: {
          speaker: 'Luca',
          line: 'You still talk like I am a kid at your table.',
        },
      },
      {
        duration: 4.4,
        camera: { focus: stagingFocus(PROLOGUE_STAGING.luca, PROLOGUE_STAGING.aldo), viewHeight: 21, ease: 'smooth' },
        text: {
          speaker: 'Aldo Vitale',
          line: 'At the docks you watch. You keep your mouth shut unless I ask.',
        },
      },
      {
        duration: 3.4,
        action: () => actions.revealShooter?.(),
        camera: { focus: new THREE.Vector3(PROLOGUE_STAGING.shooter.x, 0, PROLOGUE_STAGING.shooter.y), viewHeight: 24, ease: 'smooth' },
        text: {
          speaker: 'Barili voice',
          line: 'Tell Aldo Vitale the meeting started without him.',
        },
      },
      {
        duration: 3.2,
        action: () => {
          actions.fireShot?.();
          actions.killAldo?.();
        },
        camera: { focus: new THREE.Vector3(PROLOGUE_STAGING.aldo.x, 0, PROLOGUE_STAGING.aldo.y), viewHeight: 19, ease: 'smooth' },
        text: {
          line: 'The revolver cracks once. Aldo drops before Luca understands the sound.',
        },
      },
      {
        duration: 4.2,
        camera: { focus: new THREE.Vector3(PROLOGUE_STAGING.luca.x, 0, PROLOGUE_STAGING.luca.y), viewHeight: 31, ease: 'smooth' },
        text: {
          speaker: 'Rocco',
          line: 'Move, Luca. Whoever is still ours is bleeding out there.',
        },
      },
    ],
  };
}
